import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { Field, Form, Formik } from "formik";
import * as yup from "yup";
import { useMutation } from "@apollo/client";
import { useSnackPackContext } from "@app.automotus.io/components/context/SnackPack";
import LoadingButton from "@mui/lab/LoadingButton";
import { EnforcementIntentDetail, enforcementIntentDetailFragment } from "common/graphql";
import TextField from "@mui/material/TextField";
import { gql } from "@apollo/client";

interface UpdateNoticeNumberDialogProps {
  open: boolean;
  enforcementIntentId: string;
  noticeNumberPrefixRegex?: string;
  noticeNumberRegex: string;
  onClose: () => void;
}

interface FormValues {
  noticeNumberPrefix: string | null;
  noticeNumber: string;
}

export const UpdateNoticeNumberDialog: React.FC<UpdateNoticeNumberDialogProps> = ({
  open,
  enforcementIntentId,
  noticeNumberPrefixRegex,
  noticeNumberRegex,
  onClose,
}) => {
  const validationSchema = yup.object({
    noticeNumber: yup.string().matches(new RegExp(noticeNumberRegex)).required("Notice number is required"),
    noticeNumberPrefix: yup
      .string()
      .matches(new RegExp(noticeNumberPrefixRegex || ""))
      .required("Notice number prefix is required"),
  });

  const [updateEnforcementIntentNoticeNumber] = useMutation<
    UpdateEnforcementIntentNoticeNumberData,
    UpdateEnforcementIntentNoticeNumberVars
  >(UPDATE_ENFORCEMENT_INTENT_NOTICE_NUMBER);
  const { publishSnackbarMessage } = useSnackPackContext();

  const initialValues: FormValues = {
    noticeNumberPrefix: "",
    noticeNumber: "",
  };

  const handleSubmit = async (values: FormValues) => {
    await updateEnforcementIntentNoticeNumber({
      variables: {
        id: enforcementIntentId,
        noticeNumber: values.noticeNumber,
        noticeNumberPrefix: values.noticeNumberPrefix || undefined,
        combinedNumber: `${values.noticeNumberPrefix || ""}${values.noticeNumber}`,
      },
      onCompleted() {
        publishSnackbarMessage({
          message: "Updated notice number",
          severity: "success",
        });
        onClose();
      },
      onError(err) {
        console.error(err);
        publishSnackbarMessage({
          message: "Failed to update notice number",
          severity: "error",
        });
      },
      // TODO: validate refetch logic
    });
  };

  // TODO: disable submit button unless form is valid

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update Notice Number</DialogTitle>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {(formik) => (
          <Form>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name="noticeNumberPrefix"
                    label="Prefix"
                    fullWidth
                    error={formik.touched.noticeNumberPrefix && Boolean(formik.errors.noticeNumberPrefix)}
                    helperText={formik.touched.noticeNumberPrefix && formik.errors.noticeNumberPrefix}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name="noticeNumber"
                    label="Notice Number"
                    fullWidth
                    error={formik.touched.noticeNumber && Boolean(formik.errors.noticeNumber)}
                    helperText={formik.touched.noticeNumber && formik.errors.noticeNumber}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button fullWidth onClick={onClose}>
                Cancel
              </Button>
              <LoadingButton
                fullWidth
                type="submit"
                variant="contained"
                loading={formik.isSubmitting}
                disabled={formik.isSubmitting}
              >
                Submit
              </LoadingButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

// Will likely want to move this to a dialog
const UPDATE_ENFORCEMENT_INTENT_NOTICE_NUMBER = gql`
  mutation UpdateEnforcementIntentVehicleMake(
    $id: uuid!
    $noticeNumberPrefix: String
    $noticeNumber: String!
    $combinedNumber: String!
  ) {
    enforcementIntent: update_enforcement_intent_by_pk(
      pk_columns: { id: $id }
      _set: {
        notice_number: $combinedNumber
        officer_input_notice_number: $noticeNumber
        officer_input_notice_number_prefix: $noticeNumberPrefix
      }
    ) {
      ...enforcementIntentDetailFragment
    }
  }
  ${enforcementIntentDetailFragment}
`;

interface UpdateEnforcementIntentNoticeNumberData {
  enforcementIntent: EnforcementIntentDetail;
}

export interface UpdateEnforcementIntentNoticeNumberVars {
  id: string;
  noticeNumberPrefix?: string;
  noticeNumber: string;
  combinedNumber: string;
}

export default UpdateNoticeNumberDialog;
